import { storage } from '~/stores/LocalStorage';

export interface StoredTokens {
  access: string;
  refresh: string;
  personalData: Record<string, unknown>;
  savedAt: string;
}

export type StoredSession =
  | { status: 'signedOut' }
  | {
      status: 'signedIn';
      access: string;
      refresh: string;
      customerId: unknown;
      savedAt: string | null;
    };

interface PersistedUserStore {
  isAuthorized?: unknown;
  token?: unknown;
  refreshToken?: unknown;
  tokensSavedAt?: unknown;
  personalData?: { id?: unknown } & Record<string, unknown>;
  [key: string]: unknown;
}

export const PERSISTED_USER_STORE_KEY = 'UserStore';

const SIGNED_OUT_AT_KEY = 'auth:signedOutAt';

const readRecord = (): PersistedUserStore | null => {
  try {
    const raw = storage.getItem(PERSISTED_USER_STORE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;

    return parsed && typeof parsed === 'object'
      ? (parsed as PersistedUserStore)
      : null;
  } catch {
    return null;
  }
};

export const tokenStorage = {
  read(): StoredSession | null {
    const record = readRecord();

    if (!record) {
      return null;
    }

    const { isAuthorized, token, refreshToken, tokensSavedAt } = record;

    if (
      isAuthorized !== true ||
      typeof token !== 'string' ||
      typeof refreshToken !== 'string' ||
      !refreshToken
    ) {
      return { status: 'signedOut' };
    }

    return {
      status: 'signedIn',
      access: token,
      refresh: refreshToken,
      customerId: record.personalData?.id ?? null,
      savedAt: typeof tokensSavedAt === 'string' ? tokensSavedAt : null,
    };
  },

  write({ access, refresh, personalData, savedAt }: StoredTokens): void {
    const record = readRecord() ?? {};

    try {
      storage.setItem(
        PERSISTED_USER_STORE_KEY,
        JSON.stringify({
          ...record,
          isAuthorized: true,
          token: access,
          refreshToken: refresh,
          tokensSavedAt: savedAt,
          personalData,
        }),
      );
    } catch {
      return;
    }
  },

  signedOutAt(): string | null {
    try {
      return storage.getItem(SIGNED_OUT_AT_KEY);
    } catch {
      return null;
    }
  },

  markSignedOut(): void {
    try {
      storage.setItem(SIGNED_OUT_AT_KEY, new Date().toISOString());
    } catch {
      return;
    }
  },
};
